// FLAGS: none
// Pages "Spaces Schedules" by ISO week and narrows it by title. A session's week and day come from its
// wall-clock time in its own zone, so a late-evening session is never shifted into the browser's next day.
import { format, getISOWeek, startOfISOWeek } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { ResponseConveneHostableSessionResponse } from "@src/api/v1/data-contracts";

type Session = ResponseConveneHostableSessionResponse;

export interface HostWeek {
  /** ISO week number. */
  number: number;
  /** "yyyy-MM-dd" of the week's Monday; unique across years. */
  monday: string;
  sessions: Session[];
}

const wallClock = (session: Session) =>
  toZonedTime(session.startsAt, session.timeZone);

const byStart = (sessions: Session[]) =>
  [...sessions].sort((a, b) => a.startsAt.localeCompare(b.startsAt));

/** The weeks that hold sessions, in order. */
export const groupByWeek = (sessions: Session[]): HostWeek[] => {
  const weeks = new Map<string, HostWeek>();
  byStart(sessions).forEach((session) => {
    const local = wallClock(session);
    const monday = format(startOfISOWeek(local), "yyyy-MM-dd");
    const week = weeks.get(monday) ?? {
      number: getISOWeek(local),
      monday,
      sessions: [],
    };
    week.sessions.push(session);
    weeks.set(monday, week);
  });
  return Array.from(weeks.values());
};

/** Sessions by their local day ("yyyy-MM-dd"), in order. */
export const groupByDay = (sessions: Session[]): Array<[string, Session[]]> => {
  const days = new Map<string, Session[]>();
  byStart(sessions).forEach((session) => {
    const day = format(wallClock(session), "yyyy-MM-dd");
    days.set(day, [...(days.get(day) ?? []), session]);
  });
  return Array.from(days.entries());
};

const normalise = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/** Case- and accent-insensitive title match; a blank query matches everything. */
export const matchingTitle = (
  sessions: Session[],
  query: string,
): Session[] => {
  const needle = normalise(query.trim());
  return needle
    ? sessions.filter((session) => normalise(session.title).includes(needle))
    : sessions;
};
